import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStateScope } from '../context/StateScopeContext';
import { ArrowLeft } from 'lucide-react';
import BusinessCardContent from '../components/BusinessCardContent';

const ViewBusinessCardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isRestricted, displayName } = useStateScope();
  const wrapRef = useRef(null);
  const [centerOffset, setCenterOffset] = useState(0);

  // Force iOS safe-area (status bar / home indicator) to be black while card is shown
  useEffect(() => {
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyBg = document.body.style.background;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta ? themeMeta.getAttribute('content') : null;

    document.documentElement.style.background = '#000';
    document.body.style.background = '#000';
    if (themeMeta) themeMeta.setAttribute('content', '#000000');

    return () => {
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.background = prevBodyBg;
      if (themeMeta && prevTheme !== null) themeMeta.setAttribute('content', prevTheme);
    };
  }, []);

  // iPad portrait only: vertically center the card by adding paddingTop.
  // Re-measures on resize / orientation change. NO effect on desktop or
  // mobile (offset stays 0). NO effect on popups.
  useEffect(() => {
    if (!wrapRef.current) return;
    const isTabletPortrait = () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px) and (orientation: portrait)').matches;
    const update = () => {
      if (!wrapRef.current) { setCenterOffset(0); return; }
      if (!isTabletPortrait()) { setCenterOffset(0); return; }
      const cardEl = wrapRef.current.querySelector('[data-testid="business-card-container"]');
      if (!cardEl) { setCenterOffset(0); return; }
      const ch = cardEl.offsetHeight;
      const vh = window.innerHeight;
      // The wrapper already applies ~1rem top padding (+ safe area). We
      // split the remaining empty space evenly above and below the
      // card so it sits in the visual middle of the screen.
      const available = vh - ch - 32; // ~1rem top + ~1rem bottom breathing room
      setCenterOffset(Math.max(0, Math.round(available / 2)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapRef.current);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const cardData = {
    id: user?.id,
    name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.full_name || 'Unknown',
    title: user?.title || user?.role || '',
    role: user?.role || '',
    email: user?.email || '',
    phone: user?.personal_phone || '',
    profile_picture: user?.profile_picture || '',
    company: (isRestricted && displayName) ? displayName : 'Collective Solutions Inc.',
  };

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 z-50 bg-black overflow-y-auto flex flex-col items-center"
      data-testid="view-business-card-page"
    >
      {/* Floating back arrow — matches in-app ORB preview EXACTLY */}
      <div
        className="fixed top-0 left-0 z-[120]"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.4375rem)',
          paddingLeft: '25px',
        }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-black/80 shadow-lg"
          data-testid="card-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Card wrapper — matches OrbPreviewModal padding.
          On iPad portrait, `centerOffset` (px) is added to paddingTop so
          the card sits in the visual middle of the screen. On every
          other breakpoint centerOffset === 0 (no change). */}
      <div
        className="relative w-full flex justify-center pb-2"
        style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + 1rem + ${centerOffset}px)` }}
      >
        <BusinessCardContent cardData={cardData} compact />
      </div>
    </div>
  );
};

export default ViewBusinessCardPage;
